import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { UpdatePrompt } from '@/components/InstallApp';
import OverlayProvider from '@/components/Overlays';
import { TermsUpdateDialog } from '@/components/Privacy';
import Toaster from '@/components/Toaster';
import { cn } from '@/design-system/lib/cn';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useStore } from '@/store/AppStore';

import BottomNav from './BottomNav';
import Header from './Header';
import { PageHeaderContext, createPageHeaderStore } from './pageHeader';
import Sidebar from './Sidebar';

/**
 * Moldura das páginas: sidebar (gaveta no celular), cabeçalho, conteúdo e bottom nav.
 * As camadas globais (painel do evento, seletor, compartilhar, confirmação) e o aviso
 * ficam aqui, acima das rotas. As páginas renderizam no `<Outlet />`.
 */
export default function AppLayout() {
  const mobile = useIsMobile();
  const { actions } = useStore();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [drawer, setDrawer] = useState<string | null>(null);
  const [headerStore] = useState(createPageHeaderStore);
  const logout = actions.logout;

  // A gaveta guarda a rota em que abriu: navegar a fecha sem efeito colateral.
  const drawerOpen = mobile && drawer === pathname;

  // Cada página começa do topo, deslizando (os filtros na query não contam como troca).
  const lastPath = useRef(pathname);
  useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
  }, [pathname]);

  useEffect(() => {
    if (!drawerOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawer(null);
    };
    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  return (
    <PageHeaderContext.Provider value={headerStore.set}>
      <OverlayProvider>
        <div className="flex min-h-dvh bg-page text-ink">
          <a
            href="#main-content"
            className="absolute -left-[9999px] z-[100] rounded-control bg-white px-4 py-2 focus:top-4 focus:left-4"
          >
            Pular para o conteúdo
          </a>
          {mobile ? (
            <button
              type="button"
              aria-label="Fechar menu"
              aria-hidden={!drawerOpen}
              tabIndex={-1}
              className={cn(
                'fixed inset-0 z-46 cursor-default bg-overlay/45 transition-opacity duration-300 ease-drawer',
                drawerOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
              )}
              onClick={() => setDrawer(null)}
            />
          ) : null}
          <Sidebar
            collapsed={collapsed}
            onToggleCollapse={() => setCollapsed((c) => !c)}
            mobile={mobile}
            open={drawerOpen}
            onNavigate={() => setDrawer(null)}
            onLogout={logout}
          />
          <div className="flex min-w-0 flex-1 flex-col">
            <Header store={headerStore} onLogout={logout} />
            <main
              id="main-content"
              className="mx-auto w-full max-w-360 flex-1 px-4 pt-3 pb-26 md:px-7 md:pt-4 md:pb-10"
            >
              <Outlet />
            </main>
          </div>
        </div>
        {mobile ? <BottomNav onMore={() => setDrawer(pathname)} /> : null}
        <Toaster />
        <UpdatePrompt />
        <TermsUpdateDialog onLogout={logout} />
      </OverlayProvider>
    </PageHeaderContext.Provider>
  );
}
